import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { downloadTextFile, newIdempotencyId, parseConvexError } from "../lib/utils";
import { hasConvexConfig, hasWorkOsConfig } from "../lib/config";
import type { Id } from "../../convex/_generated/dataModel";

export function InventoryPage() {
  if (!hasConvexConfig || !hasWorkOsConfig) {
    return (
      <div className="page">
        <h1>Inventory</h1>
        <p className="lede">Configure WorkOS and Convex to browse items.</p>
      </div>
    );
  }
  return <InventoryAuthenticated />;
}

function InventoryAuthenticated() {
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState<Id<"items"> | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const data = useQuery(api.items.list, { search: q || undefined, limit: 50 });
  const detail = useQuery(api.items.get, selectedId ? { itemId: selectedId } : "skip");
  const catalogExport = useQuery(api.exports.catalogCsv);
  const inventoryExport = useQuery(api.exports.inventoryCsv);
  const createItem = useMutation(api.items.create);
  const postMovement = useMutation(api.inventory.postMovement);
  const locations = useQuery(api.locations.list);
  const [draft, setDraft] = useState({
    displayName: "",
    ean: "",
    asin: "",
    sku: "",
    reorderPoint: "",
  });
  const [adjust, setAdjust] = useState({ qty: "1", locationId: "", note: "" });

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Inventory</h1>
          <p className="lede">Item master with per-location stock, ASIN/EAN, and suppliers.</p>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="btn"
            disabled={!catalogExport}
            onClick={() =>
              catalogExport && downloadTextFile(catalogExport.fileName, catalogExport.csv)
            }
          >
            Export catalog
          </button>
          <button
            type="button"
            className="btn"
            disabled={!inventoryExport}
            onClick={() =>
              inventoryExport && downloadTextFile(inventoryExport.fileName, inventoryExport.csv)
            }
          >
            Export balances
          </button>
          <button type="button" className="btn primary" onClick={() => setShowAdd(true)}>
            Add item
          </button>
        </div>
      </div>

      <div className="toolbar">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && setQ(search.trim())}
          placeholder="Search name / EAN / ASIN / SKU"
        />
        <button type="button" className="btn" onClick={() => setQ(search.trim())}>
          Search
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="dash-grid">
        <div className="table-scroll panel" style={{ padding: 0 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>EAN</th>
                <th>ASIN</th>
                <th>On hand</th>
                <th>Locations</th>
                <th>Supplier</th>
                <th>Reorder</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((item) => (
                <tr
                  key={item._id}
                  style={{
                    cursor: "pointer",
                    background: selectedId === item._id ? "var(--brand-soft)" : undefined,
                  }}
                  onClick={() => setSelectedId(item._id)}
                >
                  <td>{item.displayName}</td>
                  <td className="mono">{item.ean ?? "—"}</td>
                  <td className="mono">{item.asin ?? "—"}</td>
                  <td>{item.onHandQty}</td>
                  <td className="tiny">
                    {item.byLocation.length === 0
                      ? "—"
                      : item.byLocation.map((l) => `${l.name}: ${l.quantity}`).join(" · ")}
                  </td>
                  <td>{item.preferredSupplier?.name ?? "—"}</td>
                  <td>{item.reorderPoint ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <section className="panel">
          <h2 className="panel-title">Item detail</h2>
          {!selectedId && <p className="muted tiny">Select an item.</p>}
          {detail && (
            <>
              <h3 style={{ margin: "0 0 0.35rem" }}>{detail.item.displayName}</h3>
              <p className="muted tiny mono">
                EAN {detail.item.ean ?? "—"} · ASIN {detail.item.asin ?? "—"} · SKU{" "}
                {detail.item.sku ?? "—"}
              </p>
              <h4 className="panel-title" style={{ marginTop: "0.85rem" }}>
                Locations
              </h4>
              <ul className="order-list">
                {detail.byLocation.map((b) => (
                  <li key={b._id} className="order-row static">
                    <span>{b.location?.name}</span>
                    <span>{b.quantity}</span>
                  </li>
                ))}
                {detail.byLocation.length === 0 && <li className="muted tiny">No balances</li>}
              </ul>
              <h4 className="panel-title">Suppliers</h4>
              <ul className="order-list">
                {detail.suppliers.map(({ link, supplier }) => (
                  <li key={link._id} className="order-row static">
                    <span>
                      {supplier.name}
                      {link.preferred ? " ★" : ""}
                    </span>
                    <span className="mono tiny">{link.supplierSku ?? ""}</span>
                  </li>
                ))}
                {detail.suppliers.length === 0 && <li className="muted tiny">No supplier links</li>}
              </ul>
              <h4 className="panel-title">Adjust stock</h4>
              <div className="toolbar">
                <select
                  className="compact-select"
                  value={adjust.locationId}
                  onChange={(e) => setAdjust((a) => ({ ...a, locationId: e.target.value }))}
                >
                  <option value="">Location</option>
                  {(locations ?? []).map((l) => (
                    <option key={l._id} value={l._id}>
                      {l.name}
                    </option>
                  ))}
                </select>
                <input
                  style={{ width: "4rem" }}
                  value={adjust.qty}
                  onChange={(e) => setAdjust((a) => ({ ...a, qty: e.target.value }))}
                  placeholder="Qty"
                />
                <button
                  type="button"
                  className="btn compact primary"
                  onClick={() => {
                    void (async () => {
                      try {
                        setError(null);
                        if (!adjust.locationId) throw new Error("Pick a location");
                        const qty = Number(adjust.qty);
                        if (!Number.isInteger(qty) || qty === 0)
                          throw new Error("Qty must be non-zero integer");
                        await postMovement({
                          kind: "adjustment",
                          lines: [
                            {
                              itemId: selectedId!,
                              quantity: Math.abs(qty),
                              toLocationId:
                                qty > 0 ? (adjust.locationId as Id<"locations">) : undefined,
                              fromLocationId:
                                qty < 0 ? (adjust.locationId as Id<"locations">) : undefined,
                              outcome: "adjustment",
                            },
                          ],
                          notes: adjust.note || "Manual adjustment",
                          idempotencyId: newIdempotencyId("adj"),
                        });
                      } catch (err) {
                        setError(
                          err instanceof Error ? err.message : parseConvexError(err).message,
                        );
                      }
                    })();
                  }}
                >
                  Post
                </button>
              </div>
            </>
          )}
        </section>
      </div>

      {showAdd && (
        <div className="modal-backdrop" role="dialog">
          <div className="modal-sheet" style={{ width: "min(520px, 100%)" }}>
            <header>
              <strong>Add item</strong>
              <button type="button" className="btn compact" onClick={() => setShowAdd(false)}>
                Close
              </button>
            </header>
            <div className="modal-body">
              <label className="field">
                <span>Name</span>
                <input
                  value={draft.displayName}
                  onChange={(e) => setDraft((d) => ({ ...d, displayName: e.target.value }))}
                />
              </label>
              <div className="header-fields">
                <label className="field">
                  <span>EAN</span>
                  <input
                    value={draft.ean}
                    onChange={(e) => setDraft((d) => ({ ...d, ean: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span>ASIN</span>
                  <input
                    value={draft.asin}
                    onChange={(e) => setDraft((d) => ({ ...d, asin: e.target.value }))}
                  />
                </label>
              </div>
              <div className="header-fields">
                <label className="field">
                  <span>SKU</span>
                  <input
                    value={draft.sku}
                    onChange={(e) => setDraft((d) => ({ ...d, sku: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span>Reorder point</span>
                  <input
                    value={draft.reorderPoint}
                    onChange={(e) => setDraft((d) => ({ ...d, reorderPoint: e.target.value }))}
                  />
                </label>
              </div>
            </div>
            <footer>
              <button type="button" className="btn" onClick={() => setShowAdd(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={() => {
                  void (async () => {
                    try {
                      setError(null);
                      const result = await createItem({
                        displayName: draft.displayName,
                        ean: draft.ean || undefined,
                        asin: draft.asin || undefined,
                        sku: draft.sku || undefined,
                        reorderPoint: draft.reorderPoint ? Number(draft.reorderPoint) : undefined,
                        idempotencyId: newIdempotencyId("item"),
                      });
                      setShowAdd(false);
                      setSelectedId(result.itemId as Id<"items">);
                      setDraft({ displayName: "", ean: "", asin: "", sku: "", reorderPoint: "" });
                    } catch (err) {
                      setError(parseConvexError(err).message);
                    }
                  })();
                }}
              >
                Create
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
